import Dexie, { type Table } from 'dexie';
import { MODULE_ENTITIES } from '@dd/shared';
import type * as T from './types';

/** Entities mirrored from the server, named exactly like the sync `entity` field (SNAPSHOT_ENTITIES). */
const CORE_STORES = {
  settings: 'id',
  money_accounts: 'id',
  products: 'id, name, barcode, deletedAt',
  product_units: 'id, productId, barcode',
  product_variants: 'id, productId, barcode',
  price_tiers: 'id, productId',
  stock_batches: 'id, productId, expiryDate',
  serial_units: 'id, productId, serialNo, status',
  customers: 'id, name, phone, deletedAt',
  vendors: 'id, name',
  sales: 'id, bizDate, customerId, status, invoiceNo, linkToken, _local',
  sale_items: 'id, saleId, productId',
  sale_payments: 'id, saleId',
  sale_returns: 'id, saleId, bizDate, _local',
  sale_return_items: 'id, returnId',
  customer_ledger: 'id, customerId, bizDate, saleId',
  installment_plans: 'id, customerId, saleId',
  installments: 'id, planId, dueDate',
  purchases: 'id, vendorId, bizDate',
  purchase_items: 'id, purchaseId, productId',
  vendor_payments: 'id, vendorId, bizDate',
  vendor_returns: 'id, vendorId, bizDate',
  vendor_return_items: 'id, vendorReturnId',
  vendor_ledger: 'id, vendorId, bizDate',
  expenses: 'id, categoryId, bizDate',
  expense_categories: 'id',
  money_movements: 'id, moneyAccountId, bizDate',
  cash_closings: 'id, moneyAccountId, bizDate',
  stock_movements: 'id, productId, bizDate',
  promotions: 'id',
  notes: 'id',
  repair_jobs: 'id, status',
  service_rates: 'id',
  bundles: 'id',
  field_defs: 'id',
  insights: 'id, status, priority',
} as const;

/** IndexedDB stores of the seven R1.1 / R1.2 categories, generated from the same spec the server uses (a field marked `idx` is indexed). */
const moduleStores = (): Record<string, string> =>
  Object.fromEntries(MODULE_ENTITIES.map((m) => [m.entity, ['id', ...new Set([...m.fields.filter((f) => f.idx || f.key === 'bizDate').map((f) => f.key), 'deletedAt'])].join(', ')]));

export const ENTITY_STORES: Readonly<Record<string, string>> & typeof CORE_STORES = { ...CORE_STORES, ...moduleStores() };
export type EntityName = keyof typeof CORE_STORES;

/** One database per shop *and* role class: a cashier device must never hold the owner's cost prices (Guide §10.8). */
export class ShopDB extends Dexie {
  settings!: Table<T.ShopSettingsRow, string>;
  money_accounts!: Table<T.MoneyAccount, string>;
  products!: Table<T.Product, string>;
  product_units!: Table<T.ProductUnit, string>;
  product_variants!: Table<T.ProductVariant, string>;
  price_tiers!: Table<T.PriceTier, string>;
  stock_batches!: Table<T.StockBatch, string>;
  serial_units!: Table<T.SerialUnit, string>;
  customers!: Table<T.Customer, string>;
  vendors!: Table<T.Vendor, string>;
  sales!: Table<T.Sale, string>;
  sale_items!: Table<T.SaleItem, string>;
  sale_payments!: Table<T.SalePayment, string>;
  sale_returns!: Table<T.SaleReturn, string>;
  sale_return_items!: Table<T.SaleReturnItem, string>;
  customer_ledger!: Table<T.LedgerEntry, string>;
  installment_plans!: Table<T.InstallmentPlan, string>;
  installments!: Table<T.Installment, string>;
  purchases!: Table<T.Purchase, string>;
  purchase_items!: Table<T.PurchaseItem, string>;
  vendor_payments!: Table<T.VendorPayment, string>;
  vendor_returns!: Table<Record<string, unknown> & { id: string }, string>;
  vendor_return_items!: Table<Record<string, unknown> & { id: string }, string>;
  vendor_ledger!: Table<T.VendorLedgerEntry, string>;
  expenses!: Table<T.Expense, string>;
  expense_categories!: Table<T.ExpenseCategory, string>;
  money_movements!: Table<T.MoneyMovement, string>;
  cash_closings!: Table<T.CashClosing, string>;
  stock_movements!: Table<T.StockMovement, string>;
  promotions!: Table<T.Promotion, string>;
  notes!: Table<T.Note, string>;
  repair_jobs!: Table<T.RepairJob, string>;
  service_rates!: Table<T.ServiceRate, string>;
  bundles!: Table<T.Bundle, string>;
  field_defs!: Table<T.FieldDef, string>;
  insights!: Table<T.Insight, string>;
  // local only
  outbox!: Table<T.OutboxItem, string>;
  sentLog!: Table<T.SentLogItem, string>;
  meta!: Table<T.MetaRow, string>;

  constructor(name: string) {
    super(name);
    const local = { outbox: 'opId, deviceSeq, state, entityId, type', sentLog: 'opId, sentAt', meta: 'key' };
    // v1 = the ten original categories; v2 adds the seven category modules (data is untouched, new stores are empty until the next sync)
    this.version(1).stores({ ...CORE_STORES, ...local });
    this.version(2).stores({ ...ENTITY_STORES, ...local });
    // v3 (app 1.1.0): same stores. Sales voided by a pre-v10 server came down with status 'completed' — fix the copies already here.
    this.version(3).stores({ ...ENTITY_STORES, ...local }).upgrade((tx) =>
      tx.table('sales').toCollection().modify((s: Record<string, unknown>) => { if (s.voidedAt && s.status !== 'voided') s.status = 'voided'; }));
  }
}

/** Device-wide store: device id, remembered sign-ins (for offline PIN unlock), preferences. */
export class GlobalDB extends Dexie {
  kv!: Table<T.MetaRow, string>;
  constructor() {
    super('dd-global');
    this.version(1).stores({ kv: 'key' });
  }
}
